import { useState } from 'react'
import { AlertCircle, CheckCircle, Clock, Zap, Sparkles, Star, Rocket, TrendingUp, TrendingDown, X } from 'lucide-react'
import { userApi, PricingPlan } from '../services/api'
import toast from 'react-hot-toast'
import { ConfirmDialog } from './ConfirmDialog'

interface SubscriptionInfo {
  userTier: string
  subscriptionStatus: string | null
  scheduledCancellation: boolean
  scheduledChangeType: 'upgrade' | 'downgrade' | 'cancel' | null
  scheduledChangeTier: 'builder' | 'launch' | 'growth' | 'scale' | null
  scheduledChangeEffectiveDate: string | null
  paddleSubscriptionId: string | null
  subscriptionStartDate: string | null
  subscriptionEndDate: string | null
}

interface SubscriptionManagerProps {
  subscriptionInfo: SubscriptionInfo | null
  onRefresh: () => void
  pricingPlans?: PricingPlan[]
}

type ConfirmAction = {
  type: 'cancel' | 'reactivate' | 'upgrade' | 'downgrade' | 'remove-scheduled'
  tier?: 'builder' | 'launch' | 'growth' | 'scale'
  priceId?: string
}

export function SubscriptionManager({ subscriptionInfo, onRefresh, pricingPlans = [] }: SubscriptionManagerProps) {
  const [loading, setLoading] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean
    action: ConfirmAction | null
    title: string
    message: string
    variant: 'danger' | 'warning' | 'info'
  }>({
    isOpen: false,
    action: null,
    title: '',
    message: '',
    variant: 'info',
  })

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const getPriceId = (tierId: string): string => {
    return pricingPlans.find(p => p.tierId === tierId)?.paddlePriceId || ''
  }

  const openConfirmDialog = (action: ConfirmAction, title: string, message: string, variant: 'danger' | 'warning' | 'info' = 'info') => {
    setConfirmDialog({
      isOpen: true,
      action,
      title,
      message,
      variant,
    })
  }

  const closeConfirmDialog = () => {
    setConfirmDialog({
      isOpen: false,
      action: null,
      title: '',
      message: '',
      variant: 'info',
    })
  }

  const handleConfirm = async () => {
    if (!confirmDialog.action) return

    setLoading(true)
    try {
      const action = confirmDialog.action

      switch (action.type) {
        case 'cancel':
          const cancelResult = await userApi.cancelSubscription()
          toast.success(cancelResult.message)
          break

        case 'reactivate':
          const reactivateResult = await userApi.reactivateSubscription()
          toast.success(reactivateResult.message)
          break

        case 'upgrade':
          if (action.priceId) {
            const upgradeResult = await userApi.upgradeSubscription(action.priceId)
            toast.success(upgradeResult.message)
          }
          break

        case 'downgrade':
          if (action.priceId) {
            const downgradeResult = await userApi.downgradeSubscription(action.priceId)
            toast.success(downgradeResult.message)
          }
          break

        case 'remove-scheduled':
          const removeResult = await userApi.removeScheduledChange()
          toast.success(removeResult.message)
          break
      }

      onRefresh()
      closeConfirmDialog()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Operation failed')
    } finally {
      setLoading(false)
    }
  }

  const currentTier = subscriptionInfo?.userTier || 'free'
  const hasActiveSubscription = subscriptionInfo?.paddleSubscriptionId && subscriptionInfo?.subscriptionStatus === 'active'

  const tierConfig = {
    builder: {
      name: 'Builder',
      icon: Star,
      color: 'from-blue-400 to-blue-500',
      credits: 100,
    },
    launch: {
      name: 'Launch',
      icon: Zap,
      color: 'from-accent to-accent-2',
      credits: 1000,
    },
    growth: {
      name: 'Growth',
      icon: Rocket,
      color: 'from-purple-500 to-pink-500',
      credits: 5000,
    },
    scale: {
      name: 'Scale',
      icon: Sparkles,
      color: 'from-orange-500 to-red-500',
      credits: 20000,
    },
  }

  const currentConfig = tierConfig[currentTier as keyof typeof tierConfig]

  // Don't render subscription management UI for free users
  if (!currentConfig) {
    return null
  }

  const TierIcon = currentConfig.icon

  return (
    <>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={handleConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        loading={loading}
        confirmText={
          confirmDialog.action?.type === 'cancel' ? 'Cancel Subscription' :
            confirmDialog.action?.type === 'reactivate' ? 'Reactivate' :
              confirmDialog.action?.type === 'upgrade' ? 'Upgrade Now' :
                confirmDialog.action?.type === 'downgrade' ? 'Schedule Downgrade' :
                  'Confirm'
        }
      />

      <div className="space-y-4">
        {/* Current Plan Card */}
        <div className="p-4 rounded-lg bg-bg border border-border">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                <TierIcon className="h-5 w-5 text-accent" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-semibold text-text">
                    {currentConfig.name} Plan
                  </h3>
                  {hasActiveSubscription && (
                    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-success/10 border border-success/20">
                      <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                      <span className="text-xs font-medium text-success">Active</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-text-muted">{currentConfig.credits} credits per month</p>
              </div>
            </div>
          </div>
        </div>

        {/* Scheduled Change Banner */}
        {subscriptionInfo?.scheduledChangeType && (
          <div className="p-4 rounded-lg bg-warning/5 border border-warning/30">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-warning/20 flex items-center justify-center flex-shrink-0">
                <Clock className="h-4 w-4 text-warning" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-text mb-1">
                  {subscriptionInfo.scheduledChangeType === 'cancel' && 'Subscription Ending'}
                  {subscriptionInfo.scheduledChangeType === 'downgrade' && 'Downgrade Scheduled'}
                  {subscriptionInfo.scheduledChangeType === 'upgrade' && 'Upgrade Scheduled'}
                </h4>
                <p className="text-xs text-text-muted mb-3">
                  {subscriptionInfo.scheduledChangeType === 'cancel' && (
                    <>Ends on <span className="font-semibold text-text">{formatDate(subscriptionInfo.scheduledChangeEffectiveDate)}</span>. You'll be on the Free plan after.</>
                  )}
                  {subscriptionInfo.scheduledChangeType === 'downgrade' && (
                    <>Changes to <span className="font-semibold text-text">{subscriptionInfo.scheduledChangeTier?.toUpperCase()}</span> on <span className="font-semibold text-text">{formatDate(subscriptionInfo.scheduledChangeEffectiveDate)}</span>.</>
                  )}
                  {subscriptionInfo.scheduledChangeType === 'upgrade' && (
                    <>Changes to <span className="font-semibold text-text">{subscriptionInfo.scheduledChangeTier?.toUpperCase()}</span> on <span className="font-semibold text-text">{formatDate(subscriptionInfo.scheduledChangeEffectiveDate)}</span>.</>
                  )}
                </p>
                <button
                  onClick={() => openConfirmDialog(
                    { type: 'remove-scheduled' },
                    'Keep Current Plan',
                    'Are you sure you want to cancel this scheduled change and keep your current plan?',
                    'info'
                  )}
                  disabled={loading}
                  className="px-3 py-1.5 bg-warning/10 hover:bg-warning/20 text-warning border border-warning/30 rounded-lg text-xs font-medium transition-all duration-200 inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  <X className="h-3 w-3" />
                  Cancel Change
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Details */}
        {hasActiveSubscription && (
          <div className="p-4 rounded-lg bg-bg border border-border">
            <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Subscription Details</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-sm text-text-muted">Status</span>
                <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${subscriptionInfo.subscriptionStatus === 'active'
                  ? 'bg-success/10 border border-success/20 text-success'
                  : subscriptionInfo.subscriptionStatus === 'canceled'
                    ? 'bg-destructive/10 border border-destructive/20 text-destructive'
                    : subscriptionInfo.subscriptionStatus === 'past_due'
                      ? 'bg-warning/10 border border-warning/20 text-warning'
                      : 'bg-border text-text-muted'
                  }`}>
                  {subscriptionInfo.subscriptionStatus === 'active' && <><div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" /> Active</>}
                  {subscriptionInfo.subscriptionStatus === 'canceled' && <><AlertCircle className="h-3 w-3" /> Canceled</>}
                  {subscriptionInfo.subscriptionStatus === 'past_due' && <><AlertCircle className="h-3 w-3" /> Past Due</>}
                  {subscriptionInfo.subscriptionStatus === 'paused' && 'Paused'}
                </div>
              </div>

              {subscriptionInfo.subscriptionEndDate && (
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-text-muted">
                    {subscriptionInfo.scheduledCancellation ? 'Access Until' : 'Next Billing'}
                  </span>
                  <span className="text-sm font-medium text-text">
                    {formatDate(subscriptionInfo.subscriptionEndDate)}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-text-muted">Auto-Renewal</span>
                <span className={`text-sm font-semibold ${!subscriptionInfo.scheduledCancellation ? 'text-success' : 'text-destructive'}`}>
                  {!subscriptionInfo.scheduledCancellation ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Plan Actions */}
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wider">Manage Plan</h4>

          {/* Upgrade Options */}
          {currentTier === 'builder' && (
            <div className="grid grid-cols-1 gap-2">
              <button
                onClick={() => {
                  const priceId = getPriceId('launch')
                  if (!priceId) {
                    toast.error('Price ID not configured')
                    return
                  }
                  openConfirmDialog(
                    { type: 'upgrade', tier: 'launch', priceId },
                    'Upgrade to Launch',
                    'Upgrade to Launch plan now? You\'ll be charged immediately with prorated billing for the current period.',
                    'info'
                  )
                }}
                disabled={loading}
                className="group px-4 py-3 bg-gradient-to-r from-accent to-accent-2 hover:from-accent/90 hover:to-accent-2/90 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-between disabled:opacity-50 shadow-lg shadow-accent/20"
              >
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  <span>Upgrade to Launch</span>
                </div>
                <TrendingUp className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={() => {
                  const priceId = getPriceId('growth')
                  if (!priceId) {
                    toast.error('Price ID not configured')
                    return
                  }
                  openConfirmDialog(
                    { type: 'upgrade', tier: 'growth', priceId },
                    'Upgrade to Growth',
                    'Upgrade to Growth plan now? You\'ll be charged immediately with prorated billing for the current period.',
                    'info'
                  )
                }}
                disabled={loading}
                className="group px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-500/90 hover:to-pink-500/90 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-between disabled:opacity-50 shadow-lg shadow-purple-500/20"
              >
                <div className="flex items-center gap-2">
                  <Rocket className="h-4 w-4" />
                  <span>Upgrade to Growth</span>
                </div>
                <TrendingUp className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={() => {
                  const priceId = getPriceId('scale')
                  if (!priceId) {
                    toast.error('Price ID not configured')
                    return
                  }
                  openConfirmDialog(
                    { type: 'upgrade', tier: 'scale', priceId },
                    'Upgrade to Scale',
                    'Upgrade to Scale plan now? You\'ll be charged immediately with prorated billing for the current period.',
                    'info'
                  )
                }}
                disabled={loading}
                className="group px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-500/90 hover:to-red-500/90 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-between disabled:opacity-50 shadow-lg shadow-orange-500/20"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  <span>Upgrade to Scale</span>
                </div>
                <TrendingUp className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          )}

          {currentTier === 'launch' && (
            <div className="space-y-2">
              <button
                onClick={() => {
                  const priceId = getPriceId('growth')
                  if (!priceId) {
                    toast.error('Price ID not configured')
                    return
                  }
                  openConfirmDialog(
                    { type: 'upgrade', tier: 'growth', priceId },
                    'Upgrade to Growth',
                    'Upgrade to Growth plan now? You\'ll be charged immediately with prorated billing for the current period.',
                    'info'
                  )
                }}
                disabled={loading}
                className="group w-full px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-500/90 hover:to-pink-500/90 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-between disabled:opacity-50 shadow-lg shadow-purple-500/20"
              >
                <div className="flex items-center gap-2">
                  <Rocket className="h-4 w-4" />
                  <span>Upgrade to Growth</span>
                </div>
                <TrendingUp className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={() => {
                  const priceId = getPriceId('scale')
                  if (!priceId) {
                    toast.error('Price ID not configured')
                    return
                  }
                  openConfirmDialog(
                    { type: 'upgrade', tier: 'scale', priceId },
                    'Upgrade to Scale',
                    'Upgrade to Scale plan now? You\'ll be charged immediately with prorated billing for the current period.',
                    'info'
                  )
                }}
                disabled={loading}
                className="group w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-500/90 hover:to-red-500/90 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-between disabled:opacity-50 shadow-lg shadow-orange-500/20"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  <span>Upgrade to Scale</span>
                </div>
                <TrendingUp className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={() => {
                  const priceId = getPriceId('builder')
                  if (!priceId) {
                    toast.error('Price ID not configured')
                    return
                  }
                  openConfirmDialog(
                    { type: 'downgrade', tier: 'builder', priceId },
                    'Downgrade to Builder',
                    'Downgrade to Builder plan? This change will take effect at the end of your current billing period. You\'ll keep Launch access until then.',
                    'warning'
                  )
                }}
                disabled={loading || subscriptionInfo?.scheduledChangeType === 'downgrade'}
                className="w-full px-4 py-3 bg-transparent hover:bg-panel border border-border text-text-muted hover:text-text rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <TrendingDown className="h-4 w-4" />
                <span>Downgrade to Builder</span>
              </button>
            </div>
          )}

          {currentTier === 'growth' && (
            <div className="space-y-2">
              <button
                onClick={() => {
                  const priceId = getPriceId('scale')
                  if (!priceId) {
                    toast.error('Price ID not configured')
                    return
                  }
                  openConfirmDialog(
                    { type: 'upgrade', tier: 'scale', priceId },
                    'Upgrade to Scale',
                    'Upgrade to Scale plan now? You\'ll be charged immediately with prorated billing for the current period.',
                    'info'
                  )
                }}
                disabled={loading}
                className="group w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-500/90 hover:to-red-500/90 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-between disabled:opacity-50 shadow-lg shadow-orange-500/20"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  <span>Upgrade to Scale</span>
                </div>
                <TrendingUp className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={() => {
                  const priceId = getPriceId('launch')
                  if (!priceId) {
                    toast.error('Price ID not configured')
                    return
                  }
                  openConfirmDialog(
                    { type: 'downgrade', tier: 'launch', priceId },
                    'Downgrade to Launch',
                    'Downgrade to Launch plan? This change will take effect at the end of your current billing period. You\'ll keep Growth access until then.',
                    'warning'
                  )
                }}
                disabled={loading || subscriptionInfo?.scheduledChangeType === 'downgrade'}
                className="w-full px-4 py-3 bg-transparent hover:bg-panel border border-border text-text-muted hover:text-text rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <TrendingDown className="h-4 w-4" />
                <span>Downgrade to Launch</span>
              </button>

              <button
                onClick={() => {
                  const priceId = getPriceId('builder')
                  if (!priceId) {
                    toast.error('Price ID not configured')
                    return
                  }
                  openConfirmDialog(
                    { type: 'downgrade', tier: 'builder', priceId },
                    'Downgrade to Builder',
                    'Downgrade to Builder plan? This change will take effect at the end of your current billing period. You\'ll keep Growth access until then.',
                    'warning'
                  )
                }}
                disabled={loading || subscriptionInfo?.scheduledChangeType === 'downgrade'}
                className="w-full px-4 py-3 bg-transparent hover:bg-panel border border-border text-text-muted hover:text-text rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <TrendingDown className="h-4 w-4" />
                <span>Downgrade to Builder</span>
              </button>
            </div>
          )}

          {currentTier === 'scale' && (
            <div className="space-y-2">
              <button
                onClick={() => {
                  const priceId = getPriceId('growth')
                  if (!priceId) {
                    toast.error('Price ID not configured')
                    return
                  }
                  openConfirmDialog(
                    { type: 'downgrade', tier: 'growth', priceId },
                    'Downgrade to Growth',
                    'Downgrade to Growth plan? This change will take effect at the end of your current billing period. You\'ll keep Scale access until then.',
                    'warning'
                  )
                }}
                disabled={loading || subscriptionInfo?.scheduledChangeType === 'downgrade'}
                className="w-full px-4 py-3 bg-transparent hover:bg-panel border border-border text-text-muted hover:text-text rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <TrendingDown className="h-4 w-4" />
                <span>Downgrade to Growth</span>
              </button>

              <button
                onClick={() => {
                  const priceId = getPriceId('launch')
                  if (!priceId) {
                    toast.error('Price ID not configured')
                    return
                  }
                  openConfirmDialog(
                    { type: 'downgrade', tier: 'launch', priceId },
                    'Downgrade to Launch',
                    'Downgrade to Launch plan? This change will take effect at the end of your current billing period. You\'ll keep Scale access until then.',
                    'warning'
                  )
                }}
                disabled={loading || subscriptionInfo?.scheduledChangeType === 'downgrade'}
                className="w-full px-4 py-3 bg-transparent hover:bg-panel border border-border text-text-muted hover:text-text rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <TrendingDown className="h-4 w-4" />
                <span>Downgrade to Launch</span>
              </button>

              <button
                onClick={() => {
                  const priceId = getPriceId('builder')
                  if (!priceId) {
                    toast.error('Price ID not configured')
                    return
                  }
                  openConfirmDialog(
                    { type: 'downgrade', tier: 'builder', priceId },
                    'Downgrade to Builder',
                    'Downgrade to Builder plan? This change will take effect at the end of your current billing period. You\'ll keep Scale access until then.',
                    'warning'
                  )
                }}
                disabled={loading || subscriptionInfo?.scheduledChangeType === 'downgrade'}
                className="w-full px-4 py-3 bg-transparent hover:bg-panel border border-border text-text-muted hover:text-text rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <TrendingDown className="h-4 w-4" />
                <span>Downgrade to Builder</span>
              </button>
            </div>
          )}

          {/* Cancel/Reactivate */}
          {hasActiveSubscription && !subscriptionInfo?.scheduledCancellation && currentTier !== 'free' && (
            <div className="pt-3 border-t border-border">
              <button
                onClick={() => openConfirmDialog(
                  { type: 'cancel' },
                  'Cancel Subscription',
                  'Are you sure you want to cancel your subscription? You\'ll keep access until the end of your current billing period.',
                  'danger'
                )}
                disabled={loading}
                className="w-full px-4 py-3 bg-destructive/10 hover:bg-destructive/20 text-destructive border border-destructive/30 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <AlertCircle className="h-4 w-4" />
                <span>Cancel Subscription</span>
              </button>
            </div>
          )}

          {subscriptionInfo?.scheduledCancellation && (
            <div className="pt-3 border-t border-border">
              <button
                onClick={() => openConfirmDialog(
                  { type: 'reactivate' },
                  'Reactivate Subscription',
                  'Reactivate your subscription? Your plan will continue and auto-renewal will be enabled.',
                  'info'
                )}
                disabled={loading}
                className="w-full px-4 py-3 bg-success/10 hover:bg-success/20 text-success border border-success/30 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <CheckCircle className="h-4 w-4" />
                <span>Reactivate Subscription</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
